import { useState, useRef, useEffect, forwardRef, useImperativeHandle } from "react";
import {
  Bold, Italic, Underline, Strikethrough,
  List, ListOrdered, AlignLeft, AlignCenter, AlignRight, AlignJustify,
  Link2, Type, Check, X,
  Undo2, Redo2, Subscript, Superscript,
  TextQuote, Minus, Indent, Outdent,
} from "lucide-react";

export interface RichTextEditorHandle {
  insertText: (text: string) => void;
  insertHTML: (html: string) => void;
}

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
  maxHeight?: number;
  "data-testid"?: string;
}

export const RichTextEditor = forwardRef<RichTextEditorHandle, RichTextEditorProps>(
  function RichTextEditor(
    {
      value,
      onChange,
      placeholder = "Type here...",
      minHeight = 120,
      maxHeight = 300,
      "data-testid": testId,
    },
    ref,
  ) {
    const editorRef = useRef<HTMLDivElement>(null);
    const [activeFormats, setActiveFormats] = useState<Set<string>>(new Set());
    const [showLinkDialog, setShowLinkDialog] = useState(false);
    const [linkUrl, setLinkUrl] = useState("https://");
    const [linkText, setLinkText] = useState("");
    const savedRangeRef = useRef<Range | null>(null);
    const fontColorRef = useRef<HTMLInputElement>(null);
    const bgColorRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
      if (editorRef.current) {
        editorRef.current.innerHTML = value;
      }
    }, []);

    useImperativeHandle(ref, () => ({
      insertText(text: string) {
        editorRef.current?.focus();
        document.execCommand("insertText", false, text);
        if (editorRef.current) onChange(editorRef.current.innerHTML);
      },
      insertHTML(html: string) {
        if (!editorRef.current) return;
        editorRef.current.innerHTML = html;
        editorRef.current.focus();
        const range = document.createRange();
        range.selectNodeContents(editorRef.current);
        range.collapse(false);
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);
        onChange(editorRef.current.innerHTML);
      },
    }));

    const checkFormats = () => {
      const formats = new Set<string>();
      try {
        if (document.queryCommandState("bold")) formats.add("bold");
        if (document.queryCommandState("italic")) formats.add("italic");
        if (document.queryCommandState("underline")) formats.add("underline");
        if (document.queryCommandState("strikeThrough")) formats.add("strikeThrough");
        if (document.queryCommandState("subscript")) formats.add("subscript");
        if (document.queryCommandState("superscript")) formats.add("superscript");
        if (document.queryCommandState("insertUnorderedList")) formats.add("insertUnorderedList");
        if (document.queryCommandState("insertOrderedList")) formats.add("insertOrderedList");
        if (document.queryCommandState("justifyLeft")) formats.add("justifyLeft");
        if (document.queryCommandState("justifyCenter")) formats.add("justifyCenter");
        if (document.queryCommandState("justifyRight")) formats.add("justifyRight");
        if (document.queryCommandState("justifyFull")) formats.add("justifyFull");
      } catch (_) {}
      setActiveFormats(formats);
    };

    const execCmd = (command: string, val?: string) => {
      editorRef.current?.focus();
      document.execCommand(command, false, val);
      if (editorRef.current) onChange(editorRef.current.innerHTML);
      checkFormats();
    };

    const insertBlockquote = () => {
      editorRef.current?.focus();
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0) {
        const range = sel.getRangeAt(0);
        const selectedText = range.toString();
        if (selectedText) {
          const bq = document.createElement("blockquote");
          bq.style.borderLeft = "3px solid #ccc";
          bq.style.paddingLeft = "1em";
          bq.style.margin = "0.5em 0";
          bq.style.color = "#666";
          range.surroundContents(bq);
        } else {
          document.execCommand("insertHTML", false, '<blockquote style="border-left:3px solid #ccc;padding-left:1em;margin:0.5em 0;color:#666">Quote</blockquote>');
        }
      } else {
        document.execCommand("insertHTML", false, '<blockquote style="border-left:3px solid #ccc;padding-left:1em;margin:0.5em 0;color:#666">Quote</blockquote>');
      }
      if (editorRef.current) onChange(editorRef.current.innerHTML);
    };

    const insertHR = () => {
      execCmd("insertHTML", '<hr style="border:none;border-top:1px solid #ccc;margin:0.75em 0" /><p><br></p>');
    };

    const handleOpenLinkDialog = (e: React.MouseEvent) => {
      e.preventDefault();
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0) {
        savedRangeRef.current = sel.getRangeAt(0).cloneRange();
        const selectedText = sel.toString().trim();
        setLinkText(selectedText || "");
      } else {
        savedRangeRef.current = null;
        setLinkText("");
      }
      setLinkUrl("https://");
      setShowLinkDialog(true);
    };

    const confirmLink = () => {
      if (!linkUrl || linkUrl === "https://") {
        setShowLinkDialog(false);
        return;
      }
      const sel = window.getSelection();
      sel?.removeAllRanges();
      editorRef.current?.focus();

      if (savedRangeRef.current) {
        sel?.addRange(savedRangeRef.current);
        const rangeText = savedRangeRef.current.toString().trim();

        if (!rangeText && linkText) {
          const a = document.createElement("a");
          a.href = linkUrl;
          a.target = "_blank";
          a.rel = "noopener noreferrer";
          a.textContent = linkText;
          savedRangeRef.current.deleteContents();
          savedRangeRef.current.insertNode(a);
          const newRange = document.createRange();
          newRange.setStartAfter(a);
          newRange.collapse(true);
          sel?.removeAllRanges();
          sel?.addRange(newRange);
        } else {
          document.execCommand("createLink", false, linkUrl);
          editorRef.current?.querySelectorAll("a[href]").forEach((a) => {
            (a as HTMLAnchorElement).target = "_blank";
            (a as HTMLAnchorElement).rel = "noopener noreferrer";
          });
        }
      }

      if (editorRef.current) onChange(editorRef.current.innerHTML);
      setShowLinkDialog(false);
      setLinkUrl("https://");
      setLinkText("");
    };

    const cancelLink = () => {
      setShowLinkDialog(false);
      setLinkUrl("https://");
      setLinkText("");
    };

    const handleInput = () => {
      if (editorRef.current) onChange(editorRef.current.innerHTML);
      checkFormats();
    };

    const handlePaste = (e: React.ClipboardEvent) => {
      const items = Array.from(e.clipboardData.items);
      const imageItem = items.find((item) => item.type.startsWith("image/"));
      if (imageItem) {
        e.preventDefault();
        const file = imageItem.getAsFile();
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => {
          document.execCommand("insertImage", false, ev.target?.result as string);
          if (editorRef.current) onChange(editorRef.current.innerHTML);
        };
        reader.readAsDataURL(file);
      }
    };

    const btn = (isActive: boolean) =>
      `p-1.5 rounded transition-colors ${
        isActive
          ? "bg-primary/10 text-primary"
          : "hover:bg-muted text-muted-foreground hover:text-foreground"
      }`;

    const sep = <div className="w-px h-4 bg-border mx-0.5" />;

    return (
      <div className="border rounded-md overflow-hidden">
        {/* Toolbar */}
        <div className="border-b bg-muted/30 px-2 py-1 flex items-center gap-0.5 flex-wrap">
          {/* Undo / Redo */}
          <button type="button" onMouseDown={(e) => { e.preventDefault(); execCmd("undo"); }} className={btn(false)} title="Undo" data-testid="rte-undo"><Undo2 className="w-3.5 h-3.5" /></button>
          <button type="button" onMouseDown={(e) => { e.preventDefault(); execCmd("redo"); }} className={btn(false)} title="Redo" data-testid="rte-redo"><Redo2 className="w-3.5 h-3.5" /></button>
          {sep}

          {/* Basic formatting */}
          <button type="button" onMouseDown={(e) => { e.preventDefault(); execCmd("bold"); }} className={btn(activeFormats.has("bold"))} title="Bold" data-testid="rte-bold"><Bold className="w-3.5 h-3.5" /></button>
          <button type="button" onMouseDown={(e) => { e.preventDefault(); execCmd("italic"); }} className={btn(activeFormats.has("italic"))} title="Italic" data-testid="rte-italic"><Italic className="w-3.5 h-3.5" /></button>
          <button type="button" onMouseDown={(e) => { e.preventDefault(); execCmd("underline"); }} className={btn(activeFormats.has("underline"))} title="Underline" data-testid="rte-underline"><Underline className="w-3.5 h-3.5" /></button>
          <button type="button" onMouseDown={(e) => { e.preventDefault(); execCmd("strikeThrough"); }} className={btn(activeFormats.has("strikeThrough"))} title="Strikethrough" data-testid="rte-strike"><Strikethrough className="w-3.5 h-3.5" /></button>
          <button type="button" onMouseDown={(e) => { e.preventDefault(); execCmd("subscript"); }} className={btn(activeFormats.has("subscript"))} title="Subscript" data-testid="rte-subscript"><Subscript className="w-3.5 h-3.5" /></button>
          <button type="button" onMouseDown={(e) => { e.preventDefault(); execCmd("superscript"); }} className={btn(activeFormats.has("superscript"))} title="Superscript" data-testid="rte-superscript"><Superscript className="w-3.5 h-3.5" /></button>
          <button type="button" onMouseDown={(e) => { e.preventDefault(); execCmd("removeFormat"); }} className={btn(false)} title="Clear Formatting" data-testid="rte-clear"><Type className="w-3.5 h-3.5" /></button>
          {sep}

          {/* Lists & indent */}
          <button type="button" onMouseDown={(e) => { e.preventDefault(); execCmd("insertUnorderedList"); }} className={btn(activeFormats.has("insertUnorderedList"))} title="Bullet List" data-testid="rte-ul"><List className="w-3.5 h-3.5" /></button>
          <button type="button" onMouseDown={(e) => { e.preventDefault(); execCmd("insertOrderedList"); }} className={btn(activeFormats.has("insertOrderedList"))} title="Numbered List" data-testid="rte-ol"><ListOrdered className="w-3.5 h-3.5" /></button>
          <button type="button" onMouseDown={(e) => { e.preventDefault(); execCmd("outdent"); }} className={btn(false)} title="Decrease Indent" data-testid="rte-outdent"><Outdent className="w-3.5 h-3.5" /></button>
          <button type="button" onMouseDown={(e) => { e.preventDefault(); execCmd("indent"); }} className={btn(false)} title="Increase Indent" data-testid="rte-indent"><Indent className="w-3.5 h-3.5" /></button>
          <button type="button" onMouseDown={(e) => { e.preventDefault(); insertBlockquote(); }} className={btn(false)} title="Blockquote" data-testid="rte-blockquote"><TextQuote className="w-3.5 h-3.5" /></button>
          {sep}

          {/* Alignment */}
          <button type="button" onMouseDown={(e) => { e.preventDefault(); execCmd("justifyLeft"); }} className={btn(activeFormats.has("justifyLeft"))} title="Align Left" data-testid="rte-align-left"><AlignLeft className="w-3.5 h-3.5" /></button>
          <button type="button" onMouseDown={(e) => { e.preventDefault(); execCmd("justifyCenter"); }} className={btn(activeFormats.has("justifyCenter"))} title="Align Center" data-testid="rte-align-center"><AlignCenter className="w-3.5 h-3.5" /></button>
          <button type="button" onMouseDown={(e) => { e.preventDefault(); execCmd("justifyRight"); }} className={btn(activeFormats.has("justifyRight"))} title="Align Right" data-testid="rte-align-right"><AlignRight className="w-3.5 h-3.5" /></button>
          <button type="button" onMouseDown={(e) => { e.preventDefault(); execCmd("justifyFull"); }} className={btn(activeFormats.has("justifyFull"))} title="Justify" data-testid="rte-justify"><AlignJustify className="w-3.5 h-3.5" /></button>
          {sep}

          {/* Link & HR */}
          <button type="button" onMouseDown={handleOpenLinkDialog} className={btn(false)} title="Insert Link" data-testid="rte-link"><Link2 className="w-3.5 h-3.5" /></button>
          <button type="button" onMouseDown={(e) => { e.preventDefault(); insertHR(); }} className={btn(false)} title="Horizontal Line" data-testid="rte-hr"><Minus className="w-3.5 h-3.5" /></button>
          {sep}

          {/* Font color */}
          <button
            type="button"
            onMouseDown={(e) => { e.preventDefault(); fontColorRef.current?.click(); }}
            className={btn(false)}
            title="Font Color"
            data-testid="rte-font-color"
          >
            <span className="flex flex-col items-center leading-none gap-px">
              <span className="text-[11px] font-bold leading-none" style={{ fontFamily: "serif" }}>A</span>
              <span className="w-3.5 h-[3px] rounded-sm bg-current" />
            </span>
          </button>
          <input
            ref={fontColorRef}
            type="color"
            className="sr-only"
            defaultValue="#000000"
            onChange={(e) => execCmd("foreColor", e.target.value)}
            data-testid="rte-font-color-input"
          />

          {/* Background / highlight color */}
          <button
            type="button"
            onMouseDown={(e) => { e.preventDefault(); bgColorRef.current?.click(); }}
            className={btn(false)}
            title="Highlight Color"
            data-testid="rte-bg-color"
          >
            <span className="flex flex-col items-center leading-none gap-px">
              <span className="text-[11px] font-bold leading-none" style={{ fontFamily: "serif" }}>A</span>
              <span className="w-3.5 h-[3px] rounded-sm bg-yellow-300" />
            </span>
          </button>
          <input
            ref={bgColorRef}
            type="color"
            className="sr-only"
            defaultValue="#ffff00"
            onChange={(e) => execCmd("hiliteColor", e.target.value)}
            data-testid="rte-bg-color-input"
          />
          {sep}

          {/* Font size */}
          <select
            className="text-xs bg-transparent border rounded px-1 py-0.5 text-muted-foreground cursor-pointer"
            onMouseDown={(e) => e.stopPropagation()}
            onChange={(e) => {
              if (e.target.value) {
                editorRef.current?.focus();
                document.execCommand("fontSize", false, e.target.value);
                if (editorRef.current) onChange(editorRef.current.innerHTML);
                e.target.value = "";
              }
            }}
            defaultValue=""
            data-testid="rte-font-size"
          >
            <option value="" disabled>Size</option>
            <option value="1">Small</option>
            <option value="3">Normal</option>
            <option value="5">Large</option>
            <option value="7">Huge</option>
          </select>
        </div>

        {/* Link insertion panel */}
        {showLinkDialog && (
          <div className="border-b bg-muted/20 px-3 py-2 flex flex-col gap-2">
            <p className="text-xs font-medium text-muted-foreground">Insert link</p>
            <div className="flex gap-2">
              <input
                type="text"
                value={linkText}
                onChange={(e) => setLinkText(e.target.value)}
                placeholder="Link text (optional)"
                className="flex-1 h-7 text-xs rounded border border-input bg-background px-2 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                data-testid="rte-link-text"
              />
              <input
                type="url"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="https://example.com"
                className="flex-[2] h-7 text-xs rounded border border-input bg-background px-2 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                data-testid="rte-link-url"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); confirmLink(); }
                  if (e.key === "Escape") cancelLink();
                }}
              />
              <button
                type="button"
                onClick={confirmLink}
                className="flex items-center gap-1 px-2 h-7 rounded bg-primary text-primary-foreground text-xs hover:bg-primary/90 shrink-0"
                data-testid="rte-link-confirm"
              ><Check className="w-3 h-3" /> Add</button>
              <button
                type="button"
                onClick={cancelLink}
                className="flex items-center px-1.5 h-7 rounded hover:bg-muted text-muted-foreground shrink-0"
                data-testid="rte-link-cancel"
              ><X className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        )}

        {/* Editor area */}
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={handleInput}
          onKeyUp={checkFormats}
          onMouseUp={checkFormats}
          onPaste={handlePaste}
          className="px-3 py-2 text-sm focus:outline-none overflow-y-auto prose prose-sm max-w-none dark:prose-invert"
          style={{ minHeight: `${minHeight}px`, maxHeight: `${maxHeight}px` }}
          data-placeholder={placeholder}
          data-testid={testId}
        />
        <style>{`
          [data-placeholder]:empty:before {
            content: attr(data-placeholder);
            color: hsl(var(--muted-foreground));
            pointer-events: none;
          }
          [contenteditable] a {
            color: hsl(var(--primary));
            text-decoration: underline;
            cursor: pointer;
          }
          [contenteditable] ul {
            list-style-type: disc;
            padding-left: 1.5em;
          }
          [contenteditable] ol {
            list-style-type: decimal;
            padding-left: 1.5em;
          }
          [contenteditable] b, [contenteditable] strong {
            font-weight: 700;
          }
          [contenteditable] i, [contenteditable] em {
            font-style: italic;
          }
          [contenteditable] u {
            text-decoration: underline;
          }
          [contenteditable] s, [contenteditable] strike {
            text-decoration: line-through;
          }
          [contenteditable] blockquote {
            border-left: 3px solid #ccc;
            padding-left: 1em;
            margin: 0.5em 0;
            color: #666;
          }
        `}</style>
      </div>
    );
  },
);
